import React, { useCallback, useEffect, useRef, useState } from "react";

import { PATHS } from "../../cloud/config";
import { useCloud } from "../../cloud/context";
import {
  createFolder,
  listDirectory,
  normalizeFilename,
  saveFile,
} from "../../cloud/storage";

import "./SaveToCloudDialog.scss";

import type { FileNode } from "../../cloud/types";

interface Props {
  excalidrawAPI: any;
  initialPrefix?: string;
  onClose: () => void;
  onSaved: (
    key: string,
    filename: string,
    etag: string | null,
  ) => Promise<void> | void;
}

export const SaveToCloudDialog: React.FC<Props> = ({
  excalidrawAPI,
  initialPrefix,
  onClose,
  onSaved,
}) => {
  const { user, currentKey, currentFilename } = useCloud();
  const personalRoot = PATHS.personal(user!.identityId);
  const sharedRoot = PATHS.shared();

  const [currentPrefix, setCurrentPrefix] = useState(
    initialPrefix ??
      (currentKey ? currentKey.replace(/[^/]+$/, "") : personalRoot),
  );
  const [nodes, setNodes] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [filename, setFilename] = useState(
    (currentFilename ?? "untitled").replace(/\.excalidraw$/, ""),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const newFolderRef = useRef<HTMLInputElement>(null);
  const filenameRef = useRef<HTMLInputElement>(null);

  const loadDir = useCallback(async (prefix: string) => {
    setLoading(true);
    try {
      const items = await listDirectory(prefix);
      setNodes(items);
    } catch {
      setError("Failed to load directory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDir(currentPrefix);
  }, [currentPrefix, loadDir]);

  useEffect(() => {
    filenameRef.current?.select();
  }, []);

  // Build breadcrumb from prefix
  const breadcrumbs = (() => {
    const isPersonal = currentPrefix.startsWith(personalRoot);
    const root = isPersonal ? personalRoot : sharedRoot;
    const rootLabel = isPersonal ? "My Files" : "Shared";
    const rest = currentPrefix.replace(root, "").replace(/\/$/, "");
    const parts = rest ? rest.split("/") : [];
    return { root, rootLabel, parts };
  })();

  const navigateTo = (prefix: string) => {
    setCurrentPrefix(prefix);
    setNewFolderMode(false);
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      return;
    }
    const prefix = `${currentPrefix}${newFolderName.trim()}/`;
    try {
      await createFolder(prefix);
      setNewFolderMode(false);
      setNewFolderName("");
      await loadDir(currentPrefix);
    } catch {
      setError("Failed to create folder");
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedName = normalizeFilename(filename.trim());
    if (!normalizedName) {
      return;
    }

    const key = `${currentPrefix}${normalizedName}`;
    setSaving(true);
    setError(null);

    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles();
      const { etag } = await saveFile(key, { elements, appState, files });
      await onSaved(key, normalizedName, etag);
    } catch {
      setError("Failed to save. Check your connection and try again.");
      setSaving(false);
    }
  };

  return (
    <div className="save-dialog-overlay" onClick={onClose}>
      <div
        className="save-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Save to cloud"
      >
        <div className="save-dialog-header">
          <h2>Save to Cloud</h2>
          <button
            className="save-dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Section tabs */}
        <div className="save-dialog-tabs">
          <button
            className={`save-dialog-tab${
              currentPrefix.startsWith(personalRoot) ? " active" : ""
            }`}
            onClick={() => navigateTo(personalRoot)}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm5 5a5 5 0 0 0-10 0h10z" />
            </svg>
            My Files
          </button>
          <button
            className={`save-dialog-tab${
              currentPrefix.startsWith(sharedRoot) ? " active" : ""
            }`}
            onClick={() => navigateTo(sharedRoot)}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
              <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-3.5 1a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm-3 1a5 5 0 0 0-2 4h1a4 4 0 0 1 2-3.444V8z" />
            </svg>
            Shared
          </button>
        </div>

        {/* Sub-path breadcrumb (only shown when inside a subfolder) */}
        {breadcrumbs.parts.length > 0 && (
          <div className="save-dialog-breadcrumb">
            <button
              className="save-dialog-crumb"
              onClick={() => navigateTo(breadcrumbs.root)}
            >
              {breadcrumbs.rootLabel}
            </button>
            {breadcrumbs.parts.map((part, i) => {
              const prefix = `${breadcrumbs.root}${breadcrumbs.parts
                .slice(0, i + 1)
                .join("/")}/`;
              return (
                <React.Fragment key={prefix}>
                  <span className="save-dialog-sep">›</span>
                  <button
                    className={`save-dialog-crumb${
                      i === breadcrumbs.parts.length - 1 ? " active" : ""
                    }`}
                    onClick={() => navigateTo(prefix)}
                  >
                    {part}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Folder listing */}
        <div className="save-dialog-body">
          {error && (
            <div className="save-dialog-error" onClick={() => setError(null)}>
              {error}
            </div>
          )}

          {loading ? (
            <div className="save-dialog-loading">Loading…</div>
          ) : (
            <div className="save-dialog-files">
              {/* Up navigation */}
              {currentPrefix !== personalRoot && currentPrefix !== sharedRoot && (
                <button
                  className="save-dialog-item save-dialog-up"
                  onClick={() => {
                    const parts = currentPrefix.replace(/\/$/, "").split("/");
                    parts.pop();
                    navigateTo(`${parts.join("/")}/`);
                  }}
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="14"
                    height="14"
                    fill="currentColor"
                    style={{ opacity: 0.5 }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                    />
                  </svg>
                  Up
                </button>
              )}

              {nodes
                .filter((n) => n.type === "folder")
                .map((node) => (
                  <button
                    key={node.key}
                    className="save-dialog-item save-dialog-folder"
                    onClick={() => navigateTo(node.key)}
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="15"
                      height="15"
                      fill="#f5a623"
                    >
                      <path d="M.54 3.87.5 3a2 2 0 0 1 2-2h3.19a2 2 0 0 1 1.45.63l.41.42H14a2 2 0 0 1 2 2v6.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3.87z" />
                    </svg>
                    {node.name}
                  </button>
                ))}

              {nodes
                .filter((n) => n.type === "file")
                .map((node) => (
                  <button
                    key={node.key}
                    className="save-dialog-item save-dialog-file"
                    onClick={() =>
                      setFilename(node.name.replace(".excalidraw", ""))
                    }
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="currentColor"
                      style={{ opacity: 0.45 }}
                    >
                      <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0H4zm0 1h5.5v3A1.5 1.5 0 0 0 11 5.5h3V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                    </svg>
                    {node.name.replace(".excalidraw", "")}
                  </button>
                ))}

              {nodes.length === 0 && (
                <span className="save-dialog-empty">Empty folder</span>
              )}
            </div>
          )}

          {/* New folder inline form */}
          {newFolderMode && (
            <form
              onSubmit={handleCreateFolder}
              className="save-dialog-newfolder"
            >
              <input
                ref={newFolderRef}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                autoFocus
              />
              <button type="submit">Create</button>
              <button type="button" onClick={() => setNewFolderMode(false)}>
                Cancel
              </button>
            </form>
          )}

          {!newFolderMode && (
            <button
              className="save-dialog-new-folder-btn"
              onClick={() => {
                setNewFolderMode(true);
                setTimeout(() => newFolderRef.current?.focus(), 50);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                width="13"
                height="13"
                fill="currentColor"
              >
                <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.825a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981l.006.139z" />
                <path d="M8.5 6.5a.5.5 0 0 0-1 0V8H6a.5.5 0 0 0 0 1h1.5v1.5a.5.5 0 0 0 1 0V9H10a.5.5 0 0 0 0-1H8.5V6.5z" />
              </svg>
              New folder
            </button>
          )}
        </div>

        {/* Filename + save */}
        <form className="save-dialog-footer" onSubmit={handleSave}>
          <label className="save-dialog-label">Filename</label>
          <div className="save-dialog-filename-row">
            <input
              ref={filenameRef}
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="untitled"
              required
            />
            <span className="save-dialog-ext">.excalidraw</span>
          </div>
          <div className="save-dialog-actions">
            <button
              type="button"
              className="save-dialog-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-dialog-save"
              disabled={saving || !filename.trim()}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
